import { KnowledgeBase, KnowledgeBaseShare } from '../services/knowledgeBaseService';

/**
 * Check if the current user is the owner of the knowledge base
 */
export function isKnowledgeBaseOwner(
  knowledgeBase: KnowledgeBase,
  currentUserId: number | undefined
): boolean {
  if (!currentUserId) return false;
  return knowledgeBase.owner_id === currentUserId;
}

/**
 * Get the share record for the current user if they have been shared this knowledge base
 */
export function getUserShare(
  knowledgeBase: KnowledgeBase,
  currentUserId: number | undefined
): KnowledgeBaseShare | null {
  if (!currentUserId || !knowledgeBase.shares) return null;
  return knowledgeBase.shares.find(share => share.shared_with_user_id === currentUserId) || null;
}

/**
 * Check if the user has read-only permission for a knowledge base
 * Returns true if the user is not the owner and has 'read' permission
 */
export function hasReadOnlyPermission(
  knowledgeBase: KnowledgeBase,
  currentUserId: number | undefined
): boolean {
  // Owner has full permissions
  if (isKnowledgeBaseOwner(knowledgeBase, currentUserId)) {
    return false;
  }

  // Check if user has share permission
  const userShare = getUserShare(knowledgeBase, currentUserId);
  if (!userShare) {
    // No share record means they don't have explicit permission
    // They might be accessing a public KB, which is read-only
    return knowledgeBase.is_public;
  }

  // User has read permission if they're not the owner and have 'read' permission
  return userShare.permission === 'read';
}

/**
 * Check if the user can edit a knowledge base
 * Returns true if the user is the owner or has 'edit' permission
 */
export function canEditKnowledgeBase(
  knowledgeBase: KnowledgeBase,
  currentUserId: number | undefined
): boolean {
  // Owner can always edit
  if (isKnowledgeBaseOwner(knowledgeBase, currentUserId)) {
    return true;
  }

  // Check if user has share permission
  const userShare = getUserShare(knowledgeBase, currentUserId);
  if (!userShare) {
    return false;
  }

  return userShare.permission === 'edit';
}

/**
 * Check if the user can share a knowledge base
 * Only the owner can share
 */
export function canShareKnowledgeBase(
  knowledgeBase: KnowledgeBase,
  currentUserId: number | undefined
): boolean {
  return isKnowledgeBaseOwner(knowledgeBase, currentUserId);
}
